import React from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { TaskNodeData } from '../TaskflowParser';

const TYPE_ICONS: Record<string, string> = {
  formTask: 'fa-clipboard-list',
  questionTask: 'fa-circle-question',
  judgePreApplicationTask: 'fa-gavel',
  verifyAccountTask: 'fa-user-check',
  authorizationTask: 'fa-shield-halved',
  confirmByApplicantTask: 'fa-check-double',
  waitForSubmitByApplicantTask: 'fa-hourglass-half',
  sendEmailTask: 'fa-envelope',
  sendApplicantMessageTask: 'fa-message',
  setStateTask: 'fa-flag',
  generateDocumentTask: 'fa-file-word',
  generateFolderTask: 'fa-folder-plus',
  generatePaymentTask: 'fa-money-bill',
  copyAttachmentsTask: 'fa-copy',
  copyFieldsTask: 'fa-clone',
  setPaidTask: 'fa-money-check',
  setTeamMemberTask: 'fa-user-plus',
  startTaskflowTask: 'fa-play',
  AITask: 'fa-robot',
  genericTask: 'fa-cog',
  unresolved: 'fa-triangle-exclamation',
};

interface TaskNodeProps extends NodeProps {
  data: TaskNodeData;
}

export function TaskNode({ data }: TaskNodeProps): React.JSX.Element {
  const icon = TYPE_ICONS[data.xsiType] ?? 'fa-cube';
  const ghost = data.xsiType === 'unresolved';
  const className = [
    'taskflow-node',
    `taskflow-node--${data.category}`,
    data.isStart ? 'taskflow-node--start' : '',
    data.inline ? 'taskflow-node--inline' : '',
    ghost ? 'taskflow-node--ghost' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <Handle type="target" position={Position.Top} />
      {data.isStart && <div className="taskflow-node__start-ribbon">▶ Start</div>}
      <div className="taskflow-node__header">
        <i className={`fa-solid ${icon}`} />
        <span className="taskflow-node__title">{data.title || data.name}</span>
      </div>
      <div className="taskflow-node__type">{data.xsiType}</div>
      {data.extras.length > 0 && (
        <ul className="taskflow-node__extras">
          {data.extras.map(e => (
            <li key={e.label}>
              <strong>{e.label}:</strong> {e.value}
            </li>
          ))}
        </ul>
      )}
      <div className="taskflow-node__chips">
        {data.assignedToGroup && <span className="chip chip--group">{data.assignedToGroup}</span>}
        {typeof data.priority === 'number' && data.priority > 0 && (
          <span className="chip chip--priority">P{data.priority}</span>
        )}
        {data.deadline && <span className="chip chip--deadline">{data.deadline}</span>}
        {data.inline && <span className="chip chip--inline">inline</span>}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}
